/**
 * ──────────────────────────────────────────────────────────────────────────
 * Reducer Utilities
 * ──────────────────────────────────────────────────────────────────────────
 * Per the official Redux docs (Splitting Up Reducer Logic — section
 * "Extracting Utility Functions"), this module contains small, reusable
 * helpers that encapsulate repeated update/transformation patterns used by
 * the slice's case reducers, keeping `caseReducers.ts` free from boilerplate.
 * https://redux.js.org/usage/structuring-reducers/splitting-reducer-logic
 * https://redux.js.org/usage/structuring-reducers/refactoring-reducer-example
 *
 * Doc: "Small utility functions containing some reusable chunk of logic
 *       that is needed in multiple places"
 *
 * Naming follows the doc's recommended file convention (`reducerUtilities.js`).
 */

import { ModelType, VariantLock, VariantPredictionToolResponse } from './types';

// convert backend score into number | string | null (accepts string or number)
export const parseScoreToNumberOrString = (
  raw?: string | number | null,
): number | string | null => {
  if (raw == null) {
    return null;
  }
  // if already a number, return as is
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null;
  }
  // try to coerce string to number
  const num = Number(raw);
  // accept only finite numeric values
  return Number.isFinite(num) ? num : raw;
};

/**
 * Legacy score parser preserving the exact behaviour of
 * `fetchSilicoMainUpdatedData.fulfilled` prior to the refactor:
 *   - null  → null
 *   - number → number (incl. NaN / Infinity, unlike `parseScoreToNumberOrString`)
 *   - string → `parseFloat(string)` (returns NaN for non-numeric strings)
 *
 * Kept separate so the two slice cases retain their original semantics:
 *   - fetchVariantPredictionTools.fulfilled used parseScoreToNumberOrString
 *   - fetchSilicoMainUpdatedData.fulfilled  used inline parseFloat (this fn)
 */
export const parseScoreLegacyParseFloat = (
  raw: string | number | null,
): number | null =>
  raw !== null ? (typeof raw === 'number' ? raw : parseFloat(raw)) : null;

/**
 * Shape used in the redux state for a prediction tool entry.
 * Kept local to avoid circular imports with `types.ts`.
 */
export type MappedPredictionTool = {
  id?: number;
  category: string;
  tool: string;
  score: number | string | null;
  prediction: string;
  calibrated_prediction: string;
  comment: string;
  model_type?: ModelType;
};

/**
 * Single-source-of-truth mapping from API response shape to redux state shape
 * for a prediction tool. Previously duplicated 3 times in
 * `fetchVariantPredictionTools.fulfilled` (Functional / Conservation /
 * Amino acid distance branches).
 */
export const mapPredictionTool = (
  tool: VariantPredictionToolResponse,
): MappedPredictionTool => ({
  id: tool.id, // WICHTIG: ID speichern
  category: tool.category,
  tool: tool.tool,
  score: parseScoreToNumberOrString(tool.score),
  prediction: tool.prediction || '',
  calibrated_prediction: tool.calibrated_prediction || '',
  comment: tool.comment || '', // ✅ comment
  model_type: tool.model_type,
});

/**
 * Variant of `mapPredictionTool` that preserves the original `parseFloat`-based
 * score handling from `fetchSilicoMainUpdatedData.fulfilled`. Note: the
 * pre-refactor code did NOT include the `id` field for these mappings, so
 * this helper omits it as well to keep behaviour identical.
 */
export const mapPredictionToolLegacyScore = (
  tool: VariantPredictionToolResponse,
): Omit<MappedPredictionTool, 'id'> => ({
  category: tool.category,
  tool: tool.tool,
  score: parseScoreLegacyParseFloat(tool.score),
  prediction: tool.prediction || '',
  calibrated_prediction: tool.calibrated_prediction || '',
  comment: tool.comment || '', // ✅ comment
  model_type: tool.model_type,
});

/**
 * Group prediction tools by category and map them to their redux shape.
 * Keeps the slice free from category-filter boilerplate.
 *
 * @param tools  - prediction tools coming from the API
 * @param mapper - mapping function (allows callers to preserve their
 *                 original score-parsing semantics; defaults to
 *                 `mapPredictionTool`)
 */
export const groupToolsByCategory = <
  T extends MappedPredictionTool | Omit<MappedPredictionTool, 'id'>,
>(
  tools: VariantPredictionToolResponse[] = [],
  mapper: (tool: VariantPredictionToolResponse) => T = mapPredictionTool as any,
) => {
  const functional: T[] = [];
  const conservation: T[] = [];
  const aminoAcidDistance: T[] = [];

  // group tools by category
  for (const tool of tools) {
    const mapped = mapper(tool);
    if (tool.category === 'Functional') {
      functional.push(mapped);
    } else if (tool.category === 'Conservation') {
      conservation.push(mapped);
    } else if (tool.category === 'Amino acid distance') {
      aminoAcidDistance.push(mapped);
    }
  }

  return { functional, conservation, aminoAcidDistance };
};

/**
 * Extract the lock owner (and build a `VariantLock`-shaped object) from a
 * rejected `acquireVariantLock` action.
 *
 * Logic and comments mirror the original inline code from
 * `acquireVariantLock.rejected` in `slice.ts`.
 *
 * Returns `null` when no lock owner can be determined (caller should leave
 * state untouched).
 */
export const extractLockedByFromError = (action: any): VariantLock | null => {
  // action.payload contains { locked_by, message } from rejectWithValue
  const payload = action?.payload;

  if (payload?.locked_by) {
    // Direct locked_by from rejectWithValue
    return {
      locked_by: payload.locked_by,
      expires_at: null,
      lock_type: payload.lock_type || 'user',
      running_task_id: payload.running_task_id,
    };
  } else {
    // Fallback: Try to parse from message string
    const message: string | undefined =
      payload?.message || payload || action?.error?.message;

    if (typeof message === 'string') {
      const match = message.match(/edited by\s+(\S+)/i);
      const locked_by = match?.[1];

      if (locked_by) {
        return { locked_by, expires_at: null };
      }
    }
  }

  return null;
};
